<template>
  <div data-private class="">

    <!-- Loading State -->
    <div v-if="loading" class="items-center justify-center flex">
      <div class="w-40 h-40">
        <Spinning_Loader />
      </div>
    </div>

    <!-- No Data State -->
    <div v-else-if="!show_data" class="text-gray-500 py-2 text-center">
      No care gap closure data available
    </div>

    <!-- Data Display -->
    <div v-else class="">
      <!-- Patient Information Section -->
      <div class="mb-3">
        <h3 class="text-sm font-semibold text-gray-900 mb-2 border-b pb-1">Patient Information</h3>
        <div class="grid grid-cols-10 gap-y-1 text-sm">
          <div class="col-span-3 text-gray-600">Patient ID:</div>
          <div class="sensitive col-span-7 font-medium">{{ getPatientData('patient_identifier') || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">Patient Name:</div>
          <div class="sensitive col-span-7 font-medium">
            {{ formatPatientName() }}
          </div>

          <div class="col-span-3 text-gray-600">DOB:</div>
          <div class="sensitive col-span-7 font-medium">{{ formatDate(getPatientData('bene_dob')) }}</div>

          <div class="col-span-3 text-gray-600">Age:</div>
          <div class="col-span-7 font-medium">{{ calculateAge(getPatientData('bene_dob')) }} years</div>

          <div class="col-span-3 text-gray-600">Gender:</div>
          <div class="col-span-7 font-medium">{{ formatGender(getPatientData('bene_sex') || taskMetadata.bene_sex_cd) }}</div>

          <div class="col-span-3 text-gray-600">Patient Phone:</div>
          <div class="sensitive col-span-7 font-medium">{{ formatPhoneNumber(getPatientData('bene_phone_number')) || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">Patient Address:</div>
          <div class="sensitive col-span-7 font-medium">{{ taskMetadata.patient_address || 'N/A' }}</div>
        </div>
      </div>

      <!-- Payor/Contract Information Section -->
      <div class="mb-3">
        <h3 class="text-sm font-semibold text-gray-900 mb-2 border-b pb-1">Payor & Contract Information</h3>
        <div class="grid grid-cols-10 gap-y-1 text-sm">
          <div class="col-span-3 text-gray-600">Payor:</div>
          <div class="sensitive col-span-7 font-medium">{{ taskMetadata.payer_name || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">LOB:</div>
          <div class="col-span-7 font-medium">{{ getPatientData('adj_lob') || taskMetadata.payer_type || taskMetadata.contract_type || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">State:</div>
          <div class="col-span-7 font-medium">{{ getPatientData('state') || taskMetadata.contract_state || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">Contract Name:</div>
          <div class="sensitive col-span-7 font-medium">{{ taskMetadata.contract_name || 'N/A' }}</div>
        </div>
      </div>

      <!-- Provider Information Section -->
      <div class="mb-3">
        <h3 class="text-sm font-semibold text-gray-900 mb-2 border-b pb-1">Provider Information</h3>
        <div class="grid grid-cols-10 gap-y-1 text-sm">
          <div class="col-span-3 text-gray-600">Provider Name:</div>
          <div class="sensitive col-span-7 font-medium">{{ taskMetadata.bene_pcp || taskMetadata.provider_name || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">Practice Name:</div>
          <div class="sensitive col-span-7 font-medium">{{ getPatientData('practice_name') || taskMetadata.practice_name || taskMetadata.tin_name || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">TIN:</div>
          <div class="sensitive col-span-7 font-medium">{{ getPatientData('tin') || taskMetadata.tin || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">NPI:</div>
          <div class="sensitive col-span-7 font-medium">{{ getPatientData('npi') || taskMetadata.npi || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">Provider Phone:</div>
          <div class="sensitive col-span-7 font-medium">{{ formatPhoneNumber(taskMetadata.provider_phone) || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">Provider Fax:</div>
          <div class="sensitive col-span-7 font-medium">{{ formatPhoneNumber(taskMetadata.provider_fax) || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">Provider Email:</div>
          <div class="sensitive col-span-7 font-medium">{{ taskMetadata.provider_email || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">EMR Access:</div>
          <div class="col-span-7 font-medium">{{ taskMetadata.emr_access || 'N/A' }}</div>

          <div class="col-span-3 text-gray-600">EHR Access:</div>
          <div class="col-span-7 font-medium">
            <span v-if="hasEHRAccess" class="text-green-600 font-semibold">✓ Available</span>
            <span v-else class="text-gray-400">Not Available</span>
          </div>

          <div class="col-span-3 text-gray-600">Patient Call Access:</div>
          <div class="col-span-7 font-medium">
            <span v-if="hasPatientCallAccess" class="text-green-600 font-semibold">✓ Available</span>
            <span v-else class="text-gray-400">Not Available</span>
          </div>
        </div>
      </div>

      <!-- Additional Information Section -->
      <div class="mb-3">
        <h3 class="text-sm font-semibold text-gray-900 mb-2 border-b pb-1">Additional Information</h3>
        <div class="grid grid-cols-10 gap-y-1 text-sm">
          <div class="col-span-3 text-gray-600">PPS/Owner:</div>
          <div class="col-span-7 font-medium">{{ taskMetadata.pps_owner || 'N/A' }}</div>

        </div>
      </div>

    </div>

  </div>
</template>

<script>
import axios from 'axios'
import Spinning_Loader from '@/components/widgetcomponents/Viz_Elements/Spinning_Loader.vue';

export default {
  name: 'Task_Care_Gap_Closure',
  components: {
    Spinning_Loader
  },
  props: {
    taskType: {
      type: Number,
      required: true
    },
    taskTypeName: {
      type: String,
      required: true
    },
    taskMetadata: {
      type: Object,
      required: true
    },
    opportunities: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      gap_data: [],
      show_data: false,
      loading: true,
      hasEHRAccess: false,
      hasPatientCallAccess: false
    }
  },
  methods: {
    // Helper method to get data from opportunities, gap_data, or taskMetadata
    getPatientData(field) {
      // First check if opportunities exist and have metadata with the field
      if (this.opportunities && this.opportunities.length > 0) {
        const metadata = this.opportunities[0].metadata || {}
        if (metadata[field]) {
          return metadata[field]
        }
      }
      // Then check if gap_data exists and has data
      if (this.gap_data && this.gap_data.length > 0 && this.gap_data[0][field]) {
        return this.gap_data[0][field]
      }
      // Fall back to taskMetadata
      return this.taskMetadata[field]
    },

    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return 'N/A'

      // Convert to string and handle float formatting
      let phoneStr = phoneNumber.toString()

      // Remove trailing .0 or other decimal digits if present
      if (phoneStr.includes('.')) {
        phoneStr = phoneStr.split('.')[0]
      }

      // Remove all non-digit characters
      const cleaned = phoneStr.replace(/\D/g, '')

      // Check if we have a valid 10-digit number
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
      }

      // If not 10 digits, return as-is or handle differently
      return phoneNumber
    },

    formatPatientName() {
      const parts = []
      // Try to get from gap_data first
      const firstName = this.getPatientData('bene_first_name')
      const lastName = this.getPatientData('bene_last_name')
      const middleName = this.taskMetadata.patient_middle_name

      if (firstName) parts.push(firstName)
      if (middleName) parts.push(middleName)
      if (lastName) parts.push(lastName)
      return parts.length > 0 ? parts.join(' ') : 'N/A'
    },

    formatGender(genderCode) {
      if (!genderCode) return 'N/A'
      const genderMap = {
        'M': 'Male',
        'F': 'Female',
        'U': 'Unknown',
        'O': 'Other'
      }
      return genderMap[genderCode.toUpperCase()] || genderCode
    },

    calculateAge(dobString) {
      if (!dobString) return 'N/A'
      const dob = new Date(dobString)
      const today = new Date()
      let age = today.getFullYear() - dob.getFullYear()
      const monthDiff = today.getMonth() - dob.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--
      }
      return age
    },

    async fetchPracticeInfoTags() {
      // Only fetch if we have a practice_entity_id
      if (!this.taskMetadata.practice_entity_id) {
        console.log('No practice_entity_id available for InfoTag fetch')
        return
      }

      try {
        const response = await axios.get('api/infoTagRelationships/', {
          params: {
            entity_id: this.taskMetadata.practice_entity_id,
            'infoTagType_names[]': 'Care Gap Practice Access'
          }
        })

        if (response?.data?.length > 0) {
          console.log('Practice InfoTags received:', response.data)

          // Check for specific tags
          response.data.forEach(tagRelationship => {
            const tagName = tagRelationship.tagName || tagRelationship.infoTag?.name

            if (tagName === 'Quality EHR Access') {
              this.hasEHRAccess = true
            } else if (tagName === 'Quality Patient Call Permission') {
              this.hasPatientCallAccess = true
            }
          })

          console.log('EHR Access:', this.hasEHRAccess, 'Patient Call Permission:', this.hasPatientCallAccess)
        }
      } catch (error) {
        console.error('Error fetching practice InfoTags:', error)
      }
    },

    async fetchCareGapData() {
      // Only fetch if we have a patient identifier
      if (!this.taskMetadata.patient_identifier) {
        this.show_data = true // Still show the basic data from taskMetadata
        this.loading = false
        return
      }

      // You may need to adjust this based on your actual metric_id for care gaps
      const request_data = {
        metric_id: 63, // Adjust this to your actual care gap metric ID
        metric_insight_id: 1,
        insight_type: 'task_insight',
        sql_filters: {
          patient_identifier: this.taskMetadata.patient_identifier,
        }
      }

      try {
        const response = await axios.post(
          'api/returnmetricinsight/',
          request_data,
        )

        if (response?.data?.length > 0) {
          this.gap_data = response.data
        }
        this.show_data = true
      } catch (error) {
        console.error('Error fetching care gap data:', error)
        // Still show the basic data even if the API call fails
        this.show_data = true
      } finally {
        this.loading = false
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'

      // Handle different date formats
      let datePart = dateString
      if (dateString.includes('T')) {
        [datePart] = dateString.split('T')
      } else if (dateString.includes(' ')) {
        [datePart] = dateString.split(' ')
      }

      const [year, month, day] = datePart.split('-').map(Number)
      const date = new Date(year, month - 1, day)
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
      })
    }
  },

  mounted() {
    this.$emit('can_submit', true, '')
    this.fetchCareGapData()
    this.fetchPracticeInfoTags()
  }
}
</script>

<style scoped>
/* Additional styling for better visual hierarchy */
h3 {
  letter-spacing: 0.025em;
}
</style>
